const SUPABASE_URL =
  process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY =
  process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;


// COURSE_IMPORT_ROLLBACK_20260920
let activeCourseCopyJournal = null;

function headers(extra = {}) {
  return {
    apikey: SUPABASE_KEY,
    Authorization:
      'Bearer ' + SUPABASE_KEY,
    ...extra
  };
}

async function readJson(
  response,
  label
) {
  const text =
    await response.text();

  if (!response.ok) {
    throw new Error(
      `${label} failed (${response.status}): ${
        text || response.statusText
      }`
    );
  }

  if (!text) {
    return [];
  }

  return JSON.parse(text);
}

async function rows(
  table,
  query,
  label
) {
  const response =
    await fetch(
      `${SUPABASE_URL}/rest/v1/${table}?${query}`,
      {
        headers: headers()
      }
    );

  return readJson(
    response,
    label
  );
}

async function insertRows(
  table,
  payload,
  label
) {
  const values =
    Array.isArray(payload)
      ? payload
      : [payload];

  if (!values.length) {
    return [];
  }

  const response =
    await fetch(
      `${SUPABASE_URL}/rest/v1/${table}`,
      {
        method: 'POST',
        headers: headers({
          'Content-Type':
            'application/json',
          Prefer:
            'return=representation'
        }),
        body: JSON.stringify(
          Array.isArray(payload)
            ? payload
            : payload
        )
      }
    );

  const result =
    await readJson(
      response,
      label
    );

  if (
    activeCourseCopyJournal
  ) {
    const createdRows =
      Array.isArray(result)
        ? result
        : (
            result
              ? [result]
              : []
          );

    createdRows.forEach(row => {
      activeCourseCopyJournal.push({
        table,
        row
      });
    });
  }

  return result;
}

async function patchRows(
  table,
  query,
  payload,
  label
) {
  const response =
    await fetch(
      `${SUPABASE_URL}/rest/v1/${table}?${query}`,
      {
        method: 'PATCH',
        headers: headers({
          'Content-Type':
            'application/json',
          Prefer:
            'return=representation'
        }),
        body: JSON.stringify(
          payload
        )
      }
    );

  return readJson(
    response,
    label
  );
}

async function callRpc(
  name,
  payload,
  label
) {
  const response =
    await fetch(
      `${SUPABASE_URL}/rest/v1/rpc/${name}`,
      {
        method: 'POST',
        headers: headers({
          'Content-Type':
            'application/json'
        }),
        body: JSON.stringify(
          payload
        )
      }
    );

  return readJson(
    response,
    label
  );
}


async function deleteRows(
  table,
  query,
  label
) {
  const response =
    await fetch(
      `${SUPABASE_URL}/rest/v1/${table}?${query}`,
      {
        method: 'DELETE',
        headers: headers({
          Prefer:
            'return=representation'
        })
      }
    );

  return readJson(
    response,
    label
  );
}


function rollbackDeleteQuery(
  row
) {
  if (
    row?.id !== null &&
    row?.id !== undefined &&
    row?.id !== ''
  ) {
    return (
      'id=eq.' +
      encodeURIComponent(
        String(row.id)
      )
    );
  }

  const identifyingKeys =
    Object.keys(
      row || {}
    )
      .filter(key => {
        if (
          key === 'course_id' ||
          key.endsWith('_id')
        ) {
          return true;
        }

        return [
          'group_number',
          'stream_number',
          'instance_number',
          'instance_order',
          'section_order',
          'display_order'
        ].includes(key);
      })
      .filter(key => {
        const value =
          row[key];

        return (
          value !== null &&
          value !== undefined &&
          value !== ''
        );
      });

  if (
    identifyingKeys.length === 0
  ) {
    throw new Error(
      'Could not identify a created row for rollback.'
    );
  }

  return identifyingKeys
    .map(
      key =>
        `${encodeURIComponent(key)}=eq.${encodeURIComponent(
          String(row[key])
        )}`
    )
    .join('&');
}


async function rollbackCourseCopy({
  targetCourseId,
  targetCourse,
  targetWeeks,
  targetWeekContent,
  journal
}) {
  const errors = [];

  const createdAssignments =
    journal
      .filter(
        entry =>
          entry.table ===
          'course_assignments'
      )
      .map(
        entry =>
          entry.row?.id
      )
      .filter(Boolean);

  const sharedTypeIds =
    journal
      .filter(
        entry =>
          entry.table ===
            'course_assignment_type_settings' &&
          entry.row
            ?.schedule_shared_across_instances ===
            true
      )
      .map(
        entry =>
          entry.row
            ?.assignment_type_id
      )
      .filter(Boolean);


  /*
   * Undo RPC-created scheduling relationships before
   * deleting the rows created by the ordinary REST calls.
   * These are deliberately best-effort: subsequent row
   * deletion is the authoritative cleanup.
   */
  for (
    const assignmentTypeId of
      [...new Set(sharedTypeIds)]
  ) {
    try {
      await callRpc(
        'set_assignment_schedule_shared_mode',
        {
          p_course_id:
            targetCourseId,
          p_assignment_type_id:
            assignmentTypeId,
          p_enabled:
            false
        },
        'Rolling back shared assignment Schedule'
      );
    } catch (err) {
      console.warn(
        'Could not explicitly disable shared Schedule during rollback:',
        err
      );
    }
  }

  for (
    const assignmentId of
      createdAssignments
  ) {
    try {
      await callRpc(
        'set_assignment_schedule_duplicate',
        {
          p_course_assignment_id:
            assignmentId,
          p_source_course_assignment_id:
            null
        },
        'Rolling back duplicated assignment Schedule'
      );
    } catch (err) {
      console.warn(
        'Could not explicitly clear Duplicate Schedule during rollback:',
        err
      );
    }
  }


  /*
   * Shared/Duplicate Schedule RPCs can create schedule
   * rows themselves, which are not part of the ordinary
   * insertion journal. Remove every Schedule row belonging
   * to the newly-created assignments before replaying the
   * journal.
   */
  const assignmentFilter =
    inFilter(
      createdAssignments
    );

  if (assignmentFilter) {
    for (
      const table of [
        'course_assignment_schedule_selections',
        'course_assignment_schedule_items'
      ]
    ) {
      try {
        await deleteRows(
          table,
          `course_assignment_id=${assignmentFilter}`,
          `Rolling back ${table}`
        );
      } catch (err) {
        errors.push(
          err?.message ||
          `Could not roll back ${table}.`
        );
      }
    }
  }


  /*
   * Delete every row created by Import in exact reverse
   * creation order. This naturally removes child rows
   * before their parents.
   */
  for (
    const entry of
      [...journal].reverse()
  ) {
    try {
      await deleteRows(
        entry.table,
        rollbackDeleteQuery(
          entry.row
        ),
        `Rolling back ${entry.table}`
      );
    } catch (err) {
      errors.push(
        err?.message ||
        `Could not roll back ${entry.table}.`
      );
    }
  }


  /*
   * Existing default week-description rows may have been
   * PATCHed rather than inserted. Restore those explicitly.
   */
  for (
    const row of
      targetWeekContent
  ) {
    try {
      const query =
        row.id
          ? `id=eq.${encodeURIComponent(row.id)}`
          : `course_week_id=eq.${encodeURIComponent(
              row.course_week_id
            )}`;

      await patchRows(
        'course_week_content',
        query,
        {
          description:
            row.description || ''
        },
        'Restoring Teaching Schedule description'
      );
    } catch (err) {
      errors.push(
        err?.message ||
        'Could not restore a Teaching Schedule description.'
      );
    }
  }


  /*
   * Restore the original week-level feature flags.
   */
  for (
    const week of
      targetWeeks
  ) {
    const flags = {};

    [
      'cq_enabled',
      'attendance_enabled',
      'pd_enabled',
      'qi_enabled'
    ].forEach(field => {
      if (
        Object.prototype
          .hasOwnProperty.call(
            week,
            field
          )
      ) {
        flags[field] =
          week[field];
      }
    });

    if (
      Object.keys(flags).length
    ) {
      try {
        await patchRows(
          'course_weeks',
          `id=eq.${encodeURIComponent(
            week.id
          )}`,
          flags,
          'Restoring course week'
        );
      } catch (err) {
        errors.push(
          err?.message ||
          'Could not restore a course week.'
        );
      }
    }
  }


  /*
   * Finally put the shell's course-level configuration
   * back exactly as it was before Import began.
   */
  try {
    await patchRows(
      'courses',
      `id=eq.${encodeURIComponent(
        targetCourseId
      )}`,
      courseConfigPayload(
        targetCourse
      ),
      'Restoring target course settings'
    );
  } catch (err) {
    errors.push(
      err?.message ||
      'Could not restore target course settings.'
    );
  }

  if (errors.length) {
    throw new Error(
      errors.join(' | ')
    );
  }
}

function inFilter(values) {
  const clean =
    Array.from(
      new Set(
        (values || [])
          .filter(
            value =>
              value !== null &&
              value !== undefined &&
              value !== ''
          )
          .map(String)
      )
    );

  return clean.length
    ? `in.(${clean
        .map(
          encodeURIComponent
        )
        .join(',')})`
    : null;
}

function omitRow(
  row,
  extra = []
) {
  const result = {
    ...(row || {})
  };

  [
    'id',
    'created_at',
    'updated_at',
    'course_weeks',
    'course_readings',
    'course_assignments',
    'attendance_groups',
    'syllabus_sections',
    'students',
    ...extra
  ].forEach(key => {
    delete result[key];
  });

  return result;
}

function dateOnly(value) {
  const match =
    String(value || '')
      .match(
        /^(\d{4})-(\d{2})-(\d{2})/
      );

  return match
    ? `${match[1]}-${match[2]}-${match[3]}`
    : null;
}

function utcDate(value) {
  const clean =
    dateOnly(value);

  if (!clean) {
    return null;
  }

  return new Date(
    `${clean}T00:00:00Z`
  );
}

function formatUtcDate(date) {
  return date
    .toISOString()
    .slice(0, 10);
}

function addDays(
  value,
  amount
) {
  const date =
    utcDate(value);

  if (!date) {
    return null;
  }

  date.setUTCDate(
    date.getUTCDate() +
      Number(amount || 0)
  );

  return formatUtcDate(date);
}

function dayDifference(
  later,
  earlier
) {
  const a =
    utcDate(later);

  const b =
    utcDate(earlier);

  if (!a || !b) {
    return 0;
  }

  return Math.round(
    (
      a.getTime() -
      b.getTime()
    ) /
      86400000
  );
}

function mondayForDate(value) {
  const date =
    utcDate(value);

  if (!date) {
    return null;
  }

  const day =
    date.getUTCDay();

  const offset =
    day === 0
      ? -6
      : 1 - day;

  date.setUTCDate(
    date.getUTCDate() +
      offset
  );

  return formatUtcDate(date);
}

function groupWeeks(weeks) {
  const grouped =
    new Map();

  (Array.isArray(weeks)
    ? weeks
    : [])
    .forEach(week => {
      const number =
        Number(
          week.week_number
        );

      if (
        !grouped.has(
          number
        )
      ) {
        grouped.set(
          number,
          []
        );
      }

      grouped
        .get(number)
        .push(week);
    });

  grouped.forEach(list => {
    list.sort(
      (a, b) =>
        String(
          a.class_date || ''
        ).localeCompare(
          String(
            b.class_date || ''
          )
        )
    );
  });

  return grouped;
}

function buildWeekMapping(
  sourceWeeks,
  targetWeeks
) {
  const sourceGroups =
    groupWeeks(
      sourceWeeks
    );

  const targetGroups =
    groupWeeks(
      targetWeeks
    );

  const bySourceId =
    new Map();

  const unmatched = [];

  sourceGroups.forEach(
    (
      sourceRows,
      weekNumber
    ) => {
      const targetRows =
        targetGroups.get(
          weekNumber
        ) || [];

      sourceRows.forEach(
        (
          sourceWeek,
          index
        ) => {
          const targetWeek =
            targetRows[index] ||
            null;

          if (targetWeek) {
            bySourceId.set(
              String(
                sourceWeek.id
              ),
              targetWeek
            );
          } else {
            unmatched.push({
              sourceWeek,
              weekNumber,
              meetingOrdinal:
                index + 1
            });
          }
        }
      );
    }
  );

  return {
    bySourceId,
    unmatched,
    sourceGroups,
    targetGroups
  };
}

function mapCourseDate(
  value,
  sourceGroups,
  targetGroups
) {
  const clean =
    dateOnly(value);

  if (!clean) {
    return value || null;
  }

  const sourceDate =
    utcDate(clean);

  const orderedSourceWeeks =
    [...sourceGroups.entries()]
      .sort(
        (a, b) =>
          Number(a[0]) -
          Number(b[0])
      )
      .map(
        ([weekNumber, list]) => ({
          weekNumber,
          monday:
            mondayForDate(
              list[0]
                ?.class_date
            )
        })
      )
      .filter(
        item =>
          item.monday
      );

  for (
    const sourceWeek of
      orderedSourceWeeks
  ) {
    const sourceMonday =
      utcDate(
        sourceWeek.monday
      );

    const sourceSunday =
      utcDate(
        addDays(
          sourceWeek.monday,
          6
        )
      );

    if (
      sourceDate >=
        sourceMonday &&
      sourceDate <=
        sourceSunday
    ) {
      const targetList =
        targetGroups.get(
          sourceWeek.weekNumber
        ) || [];

      if (
        !targetList.length
      ) {
        return null;
      }

      const targetMonday =
        mondayForDate(
          targetList[0]
            .class_date
        );

      return addDays(
        targetMonday,
        dayDifference(
          clean,
          sourceWeek.monday
        )
      );
    }
  }

  const firstSource =
    orderedSourceWeeks[0];

  if (!firstSource) {
    return clean;
  }

  const firstTargetList =
    targetGroups.get(
      firstSource.weekNumber
    ) || [];

  if (!firstTargetList.length) {
    return null;
  }

  const firstTargetMonday =
    mondayForDate(
      firstTargetList[0]
        .class_date
    );

  return addDays(
    firstTargetMonday,
    dayDifference(
      clean,
      firstSource.monday
    )
  );
}

function mapRelativeToWeek(
  value,
  sourceWeek,
  targetWeek
) {
  const clean =
    dateOnly(value);

  if (!clean) {
    return value || null;
  }

  return addDays(
    targetWeek.class_date,
    dayDifference(
      clean,
      sourceWeek.class_date
    )
  );
}

function stripHtml(value) {
  return String(
    value || ''
  )
    .replace(
      /<[^>]*>/g,
      ' '
    )
    .replace(
      /\s+/g,
      ' '
    )
    .trim();
}

function isDefaultWeekContent(
  row
) {
  const text =
    stripHtml(
      row?.description
    );

  return (
    !text ||
    text ===
      'Self-Directed Study Week'
  );
}

async function fetchCourse(
  courseId
) {
  const result =
    await rows(
      'courses',
      `id=eq.${encodeURIComponent(
        courseId
      )}&select=*&limit=1`,
      'Loading course'
    );

  return result?.[0] || null;
}

async function fetchWeeks(
  courseId
) {
  return rows(
    'course_weeks',
    `course_id=eq.${encodeURIComponent(
      courseId
    )}&select=*&order=week_number.asc,class_date.asc`,
    'Loading course weeks'
  );
}

async function fetchByWeekIds(
  table,
  weekIds,
  label
) {
  const filter =
    inFilter(
      weekIds
    );

  if (!filter) {
    return [];
  }

  return rows(
    table,
    `course_week_id=${filter}&select=*`,
    label
  );
}

async function fetchTargetShellState(
  targetCourse,
  targetWeeks
) {
  const weekIds =
    targetWeeks
      .map(week => week.id)
      .filter(Boolean);

  const [
    enrolments,
    assignments,
    syllabusSections,
    readings,
    aiPermissions,
    attendanceGroups,
    streamLabels,
    groupLabels,
    typeSettings,
    typeDescriptions,
    typeWeights,
    weekContent,
    weekReadings,
    events
  ] =
    await Promise.all([
      rows(
        'enrolments',
        `course_id=eq.${encodeURIComponent(
          targetCourse.id
        )}&active=eq.true&select=id,student_id,students(is_master)`,
        'Checking target students'
      ),

      rows(
        'course_assignments',
        `course_id=eq.${encodeURIComponent(
          targetCourse.id
        )}&select=id&limit=1`,
        'Checking target assignments'
      ),

      rows(
        'course_syllabus_sections',
        `course_id=eq.${encodeURIComponent(
          targetCourse.id
        )}&select=id&limit=1`,
        'Checking target syllabus'
      ),

      rows(
        'course_readings',
        `course_id=eq.${encodeURIComponent(
          targetCourse.id
        )}&select=id&limit=1`,
        'Checking target readings'
      ),

      rows(
        'course_ai_permissions',
        `course_id=eq.${encodeURIComponent(
          targetCourse.id
        )}&select=course_id&limit=1`,
        'Checking target AI permissions'
      ),

      rows(
        'attendance_groups',
        `course_id=eq.${encodeURIComponent(
          targetCourse.id
        )}&select=id&limit=1`,
        'Checking target attendance groups'
      ),

      rows(
        'course_stream_labels',
        `course_id=eq.${encodeURIComponent(
          targetCourse.id
        )}&select=stream_number&limit=1`,
        'Checking target Stream labels'
      ),

      rows(
        'course_group_labels',
        `course_id=eq.${encodeURIComponent(
          targetCourse.id
        )}&select=group_number&limit=1`,
        'Checking target Group labels'
      ),

      rows(
        'course_assignment_type_settings',
        `course_id=eq.${encodeURIComponent(
          targetCourse.id
        )}&select=assignment_type_id&limit=1`,
        'Checking target assignment settings'
      ),

      rows(
        'course_assignment_type_descriptions',
        `course_id=eq.${encodeURIComponent(
          targetCourse.id
        )}&select=assignment_type_id&limit=1`,
        'Checking target assignment descriptions'
      ),

      rows(
        'course_assignment_type_weights',
        `course_id=eq.${encodeURIComponent(
          targetCourse.id
        )}&select=assignment_type_id&limit=1`,
        'Checking target assignment weights'
      ),

      fetchByWeekIds(
        'course_week_content',
        weekIds,
        'Checking target week descriptions'
      ),

      fetchByWeekIds(
        'course_week_reading_assignments',
        weekIds,
        'Checking target week readings'
      ),

      fetchByWeekIds(
        'course_events',
        weekIds,
        'Checking target Events'
      )
    ]);

  const realStudents =
    (
      Array.isArray(enrolments)
        ? enrolments
        : []
    ).filter(
      row =>
        row?.students
          ?.is_master !==
        true
    );

  const nonDefaultWeekContent =
    (
      Array.isArray(weekContent)
        ? weekContent
        : []
    ).filter(
      row =>
        !isDefaultWeekContent(
          row
        )
    );

  const contentKinds = [];

  if (assignments.length) {
    contentKinds.push(
      'assignments'
    );
  }

  if (
    syllabusSections.length
  ) {
    contentKinds.push(
      'syllabus'
    );
  }

  if (readings.length) {
    contentKinds.push(
      'readings'
    );
  }

  if (
    aiPermissions.length
  ) {
    contentKinds.push(
      'AI permissions'
    );
  }

  if (
    attendanceGroups.length
  ) {
    contentKinds.push(
      'attendance groups'
    );
  }

  if (
    streamLabels.length ||
    groupLabels.length
  ) {
    contentKinds.push(
      'Stream / Group configuration'
    );
  }

  if (
    typeSettings.length ||
    typeDescriptions.length ||
    typeWeights.length
  ) {
    contentKinds.push(
      'assignment configuration'
    );
  }

  if (
    nonDefaultWeekContent.length
  ) {
    contentKinds.push(
      'Teaching Schedule descriptions'
    );
  }

  if (
    weekReadings.length
  ) {
    contentKinds.push(
      'scheduled readings'
    );
  }

  if (events.length) {
    contentKinds.push(
      'Events'
    );
  }

  return {
    realStudents,
    contentKinds
  };
}

export async function previewCourseCopy({
  sourceCourseId,
  targetCourseId
}) {
  if (
    !sourceCourseId ||
    !targetCourseId
  ) {
    throw new Error(
      'Choose a source and target course.'
    );
  }

  if (
    String(sourceCourseId) ===
    String(targetCourseId)
  ) {
    throw new Error(
      'A course cannot import from itself.'
    );
  }

  const [
    sourceCourse,
    targetCourse,
    sourceWeeks,
    targetWeeks
  ] =
    await Promise.all([
      fetchCourse(
        sourceCourseId
      ),
      fetchCourse(
        targetCourseId
      ),
      fetchWeeks(
        sourceCourseId
      ),
      fetchWeeks(
        targetCourseId
      )
    ]);

  if (
    !sourceCourse ||
    !targetCourse
  ) {
    throw new Error(
      'One of the selected courses could not be found.'
    );
  }

  const mapping =
    buildWeekMapping(
      sourceWeeks,
      targetWeeks
    );

  const sourceWeekIds =
    sourceWeeks
      .map(week => week.id)
      .filter(Boolean);

  const [
    targetState,
    sourceAssignments,
    sourceReadings,
    sourceWeekContent,
    sourceWeekReadings,
    sourceEvents,
    sourceSyllabus
  ] =
    await Promise.all([
      fetchTargetShellState(
        targetCourse,
        targetWeeks
      ),

      rows(
        'course_assignments',
        `course_id=eq.${encodeURIComponent(
          sourceCourseId
        )}&select=id`,
        'Counting source assignments'
      ),

      rows(
        'course_readings',
        `course_id=eq.${encodeURIComponent(
          sourceCourseId
        )}&select=id`,
        'Counting source readings'
      ),

      fetchByWeekIds(
        'course_week_content',
        sourceWeekIds,
        'Counting source week descriptions'
      ),

      fetchByWeekIds(
        'course_week_reading_assignments',
        sourceWeekIds,
        'Counting source scheduled readings'
      ),

      fetchByWeekIds(
        'course_events',
        sourceWeekIds,
        'Counting source Events'
      ),

      rows(
        'course_syllabus_sections',
        `course_id=eq.${encodeURIComponent(
          sourceCourseId
        )}&select=id`,
        'Counting source syllabus sections'
      )
    ]);

  const blockers = [];

  if (
    targetCourse.is_live ===
    true
  ) {
    blockers.push(
      'The target course is live.'
    );
  }

  if (
    targetState
      .realStudents
      .length
  ) {
    blockers.push(
      'The target course already has real student enrolments.'
    );
  }

  if (
    targetState
      .contentKinds
      .length
  ) {
    blockers.push(
      'The target is not an empty course shell: ' +
      targetState
        .contentKinds
        .join(', ') +
      '.'
    );
  }

  if (
    !targetWeeks.length
  ) {
    blockers.push(
      'The target course has no teaching weeks.'
    );
  }

  if (
    mapping.unmatched.length
  ) {
    const labels =
      mapping.unmatched
        .slice(0, 12)
        .map(
          item =>
            `Week ${
              item.weekNumber
            }, meeting ${
              item.meetingOrdinal
            }`
        );

    blockers.push(
      'The target has fewer class meetings than the source for: ' +
      labels.join(', ') +
      (
        mapping
          .unmatched
          .length > 12
          ? '…'
          : ''
      ) +
      '.'
    );
  }

  return {
    sourceCourse,
    targetCourse,
    sourceWeeks,
    targetWeeks,
    mapping,
    blockers,
    summary: {
      assignments:
        sourceAssignments.length,
      readings:
        sourceReadings.length,
      scheduledReadings:
        sourceWeekReadings.length,
      events:
        sourceEvents.length,
      weekDescriptions:
        sourceWeekContent.length,
      syllabusSections:
        sourceSyllabus.length
    }
  };
}

async function upsertWeekContent(
  targetWeekId,
  description
) {
  const existing =
    await rows(
      'course_week_content',
      `course_week_id=eq.${encodeURIComponent(
        targetWeekId
      )}&select=id&limit=1`,
      'Checking target week description'
    );

  if (
    existing?.[0]?.id
  ) {
    await patchRows(
      'course_week_content',
      `id=eq.${encodeURIComponent(
        existing[0].id
      )}`,
      {
        description:
          description || ''
      },
      'Updating target week description'
    );

    return;
  }

  await insertRows(
    'course_week_content',
    {
      course_week_id:
        targetWeekId,
      description:
        description || ''
    },
    'Creating target week description'
  );
}

async function copyOneToOneRows(
  table,
  sourceRows,
  transform,
  label
) {
  const map =
    new Map();

  for (
    const sourceRow of
      sourceRows
  ) {
    const payload =
      transform(
        sourceRow
      );

    if (!payload) {
      continue;
    }

    const created =
      await insertRows(
        table,
        payload,
        label
      );

    const targetRow =
      created?.[0] || null;

    if (
      sourceRow?.id &&
      targetRow?.id
    ) {
      map.set(
        String(
          sourceRow.id
        ),
        targetRow
      );
    }
  }

  return map;
}

function courseConfigPayload(
  sourceCourse
) {
  const fields = [
    'pd_due_day',
    'cq_clarificatory_enabled',
    'cq_clarificatory_allowed',
    'cq_due_hours_before_class',
    'qi_enabled',
    'qi_groups_per_stream',
    'streams_enabled',
    'group_based_attendance',
    'automated_checkin_enabled',
    'attendance_grace_absences',
    'attendance_min_contribution',
    'attendance_penalty_pattern',
    'attendance_penalty_increase_percent',
    'grading_mode',
    'pd_enabled',
    'cq_enabled',
    'voting_enabled',
    'attendance_enabled',
    'self_eval_enabled'
  ];

  const payload = {};

  fields.forEach(field => {
    if (
      Object.prototype
        .hasOwnProperty.call(
          sourceCourse,
          field
        )
    ) {
      payload[field] =
        sourceCourse[field];
    }
  });

  return payload;
}

export async function copyCourseContent({
  sourceCourseId,
  targetCourseId
}) {
  const preview =
    await previewCourseCopy({
      sourceCourseId,
      targetCourseId
    });

  if (
    preview.blockers.length
  ) {
    throw new Error(
      preview.blockers.join(' ')
    );
  }

  const {
    sourceCourse,
    targetCourse,
    sourceWeeks,
    targetWeeks,
    mapping
  } = preview;

  const sourceWeekIds =
    sourceWeeks
      .map(week => week.id)
      .filter(Boolean);

  const [
    sourceWeekContent,
    sourceReadings,
    sourceWeekReadings,
    sourceEvents,
    sourceSequences,
    sourceAssignments,
    sourceTypeSettings,
    sourceTypeDescriptions,
    sourceTypeWeights,
    sourceSyllabusSections,
    sourceSyllabusContent,
    sourceAiPermissions,
    sourceAttendanceGroups,
    sourceStreamLabels,
    sourceGroupLabels,
    duplicateLinks
  ] =
    await Promise.all([
      fetchByWeekIds(
        'course_week_content',
        sourceWeekIds,
        'Loading source week descriptions'
      ),

      rows(
        'course_readings',
        `course_id=eq.${encodeURIComponent(
          sourceCourseId
        )}&select=*`,
        'Loading source readings'
      ),

      fetchByWeekIds(
        'course_week_reading_assignments',
        sourceWeekIds,
        'Loading source reading schedule'
      ),

      fetchByWeekIds(
        'course_events',
        sourceWeekIds,
        'Loading source Events'
      ),

      rows(
        'course_assignment_sequences',
        `course_id=eq.${encodeURIComponent(
          sourceCourseId
        )}&select=*`,
        'Loading source assignment sequences'
      ),

      rows(
        'course_assignments',
        `course_id=eq.${encodeURIComponent(
          sourceCourseId
        )}&select=*&order=instance_number.asc`,
        'Loading source assignments'
      ),

      rows(
        'course_assignment_type_settings',
        `course_id=eq.${encodeURIComponent(
          sourceCourseId
        )}&select=*`,
        'Loading source assignment settings'
      ),

      rows(
        'course_assignment_type_descriptions',
        `course_id=eq.${encodeURIComponent(
          sourceCourseId
        )}&select=*`,
        'Loading source assignment descriptions'
      ),

      rows(
        'course_assignment_type_weights',
        `course_id=eq.${encodeURIComponent(
          sourceCourseId
        )}&select=*`,
        'Loading source assignment weights'
      ),

      rows(
        'course_syllabus_sections',
        `course_id=eq.${encodeURIComponent(
          sourceCourseId
        )}&select=*`,
        'Loading source syllabus sections'
      ),

      rows(
        'course_syllabus_content',
        `course_id=eq.${encodeURIComponent(
          sourceCourseId
        )}&select=*`,
        'Loading source syllabus content'
      ),

      rows(
        'course_ai_permissions',
        `course_id=eq.${encodeURIComponent(
          sourceCourseId
        )}&select=*`,
        'Loading source AI permissions'
      ),

      rows(
        'attendance_groups',
        `course_id=eq.${encodeURIComponent(
          sourceCourseId
        )}&select=*`,
        'Loading source attendance groups'
      ),

      rows(
        'course_stream_labels',
        `course_id=eq.${encodeURIComponent(
          sourceCourseId
        )}&select=*`,
        'Loading source Stream labels'
      ),

      rows(
        'course_group_labels',
        `course_id=eq.${encodeURIComponent(
          sourceCourseId
        )}&select=*`,
        'Loading source Group labels'
      ),

      rows(
        'course_assignment_schedule_duplicate_details',
        `course_id=eq.${encodeURIComponent(
          sourceCourseId
        )}&select=*`,
        'Loading source duplicated assignment schedules'
      )
    ]);

  const targetWeekIds =
    targetWeeks
      .map(
        week =>
          week.id
      )
      .filter(Boolean);

  const targetWeekContent =
    await fetchByWeekIds(
      'course_week_content',
      targetWeekIds,
      'Snapshotting target Teaching Schedule descriptions'
    );

  if (
    activeCourseCopyJournal
  ) {
    throw new Error(
      'Another course import is already running in this browser.'
    );
  }

  const rollbackJournal = [];

  activeCourseCopyJournal =
    rollbackJournal;

  try {

  await patchRows(
    'courses',
    `id=eq.${encodeURIComponent(
      targetCourseId
    )}`,
    courseConfigPayload(
      sourceCourse
    ),
    'Copying course settings'
  );

  for (
    const sourceWeek of
      sourceWeeks
  ) {
    const targetWeek =
      mapping.bySourceId.get(
        String(
          sourceWeek.id
        )
      );

    if (!targetWeek) {
      continue;
    }

    const flags = {};

    [
      'cq_enabled',
      'attendance_enabled',
      'pd_enabled',
      'qi_enabled'
    ].forEach(field => {
      if (
        Object.prototype
          .hasOwnProperty.call(
            sourceWeek,
            field
          )
      ) {
        flags[field] =
          sourceWeek[field];
      }
    });

    if (
      Object.keys(flags)
        .length
    ) {
      await patchRows(
        'course_weeks',
        `id=eq.${encodeURIComponent(
          targetWeek.id
        )}`,
        flags,
        'Copying week settings'
      );
    }
  }

  for (
    const sourceContent of
      sourceWeekContent
  ) {
    const targetWeek =
      mapping.bySourceId.get(
        String(
          sourceContent
            .course_week_id
        )
      );

    if (!targetWeek) {
      continue;
    }

    await upsertWeekContent(
      targetWeek.id,
      sourceContent
        .description
    );
  }

  if (
    sourceSyllabusSections.length
  ) {
    await insertRows(
      'course_syllabus_sections',
      sourceSyllabusSections.map(
        row => ({
          ...omitRow(row),
          course_id:
            targetCourseId
        })
      ),
      'Copying syllabus sections'
    );
  }

  if (
    sourceSyllabusContent.length
  ) {
    await insertRows(
      'course_syllabus_content',
      sourceSyllabusContent.map(
        row => ({
          ...omitRow(row),
          course_id:
            targetCourseId
        })
      ),
      'Copying syllabus content'
    );
  }

  if (
    sourceAiPermissions.length
  ) {
    await insertRows(
      'course_ai_permissions',
      sourceAiPermissions.map(
        row => ({
          ...omitRow(row),
          course_id:
            targetCourseId
        })
      ),
      'Copying AI permissions'
    );
  }

  const sequenceMap =
    await copyOneToOneRows(
      'course_assignment_sequences',
      sourceSequences,
      row => ({
        ...omitRow(row),
        course_id:
          targetCourseId
      }),
      'Copying assignment sequence'
    );

  const readingMap =
    await copyOneToOneRows(
      'course_readings',
      sourceReadings,
      row => ({
        ...omitRow(row),
        course_id:
          targetCourseId
      }),
      'Copying course reading'
    );

  const weekReadingMap =
    new Map();

  for (
    const sourceLink of
      sourceWeekReadings
  ) {
    const targetWeek =
      mapping.bySourceId.get(
        String(
          sourceLink
            .course_week_id
        )
      );

    const targetReading =
      readingMap.get(
        String(
          sourceLink
            .course_reading_id
        )
      );

    if (
      !targetWeek ||
      !targetReading?.id
    ) {
      continue;
    }

    const created =
      await insertRows(
        'course_week_reading_assignments',
        {
          ...omitRow(
            sourceLink,
            [
              'course_week_id',
              'course_reading_id'
            ]
          ),
          course_week_id:
            targetWeek.id,
          course_reading_id:
            targetReading.id
        },
        'Copying scheduled reading'
      );

    if (
      sourceLink.id &&
      created?.[0]?.id
    ) {
      weekReadingMap.set(
        String(
          sourceLink.id
        ),
        created[0]
      );
    }
  }

  const eventMap =
    new Map();

  for (
    const sourceEvent of
      sourceEvents
  ) {
    const sourceWeek =
      sourceWeeks.find(
        week =>
          String(week.id) ===
          String(
            sourceEvent
              .course_week_id
          )
      );

    const targetWeek =
      mapping.bySourceId.get(
        String(
          sourceEvent
            .course_week_id
        )
      );

    if (
      !sourceWeek ||
      !targetWeek
    ) {
      continue;
    }

    const payload = {
      ...omitRow(
        sourceEvent,
        [
          'course_week_id',
          'event_date'
        ]
      ),
      course_week_id:
        targetWeek.id,
      event_date:
        sourceEvent.event_date
          ? mapRelativeToWeek(
              sourceEvent
                .event_date,
              sourceWeek,
              targetWeek
            )
          : null
    };

    const created =
      await insertRows(
        'course_events',
        payload,
        'Copying Event'
      );

    if (
      sourceEvent.id &&
      created?.[0]?.id
    ) {
      eventMap.set(
        String(
          sourceEvent.id
        ),
        created[0]
      );
    }
  }

  const assignmentMap =
    new Map();

  const feedbackLinks = [];

  for (
    const sourceAssignment of
      sourceAssignments
  ) {
    const payload = {
      ...omitRow(
        sourceAssignment,
        [
          'course_id',
          'open_date',
          'due_date',
          'occurs_on',
          'assigned_instructor_id',
          'feedback_available_in_assignment_id'
        ]
      ),

      course_id:
        targetCourseId,

      open_date:
        sourceAssignment
          .open_date
          ? mapCourseDate(
              sourceAssignment
                .open_date,
              mapping
                .sourceGroups,
              mapping
                .targetGroups
            )
          : null,

      due_date:
        sourceAssignment
          .due_date
          ? mapCourseDate(
              sourceAssignment
                .due_date,
              mapping
                .sourceGroups,
              mapping
                .targetGroups
            )
          : null,

      occurs_on:
        sourceAssignment
          .occurs_on
          ? mapCourseDate(
              sourceAssignment
                .occurs_on,
              mapping
                .sourceGroups,
              mapping
                .targetGroups
            )
          : null,

      assigned_instructor_id:
        null
    };

    const created =
      await insertRows(
        'course_assignments',
        payload,
        'Copying assignment'
      );

    const targetAssignment =
      created?.[0] || null;

    if (
      sourceAssignment.id &&
      targetAssignment?.id
    ) {
      assignmentMap.set(
        String(
          sourceAssignment.id
        ),
        targetAssignment
      );

      if (
        sourceAssignment
          .feedback_available_in_assignment_id
      ) {
        feedbackLinks.push({
          targetAssignmentId:
            targetAssignment.id,
          sourceFeedbackAssignmentId:
            sourceAssignment
              .feedback_available_in_assignment_id
        });
      }
    }
  }

  for (
    const link of
      feedbackLinks
  ) {
    const targetFeedback =
      assignmentMap.get(
        String(
          link
            .sourceFeedbackAssignmentId
        )
      );

    if (
      targetFeedback?.id
    ) {
      await patchRows(
        'course_assignments',
        `id=eq.${encodeURIComponent(
          link
            .targetAssignmentId
        )}`,
        {
          feedback_available_in_assignment_id:
            targetFeedback.id
        },
        'Copying assignment feedback-release link'
      );
    }
  }

  if (
    sourceTypeSettings.length
  ) {
    await insertRows(
      'course_assignment_type_settings',
      sourceTypeSettings.map(
        row => {
          const payload = {
            ...omitRow(row),
            course_id:
              targetCourseId
          };

          if (
            row.sequence_id
          ) {
            payload.sequence_id =
              sequenceMap.get(
                String(
                  row.sequence_id
                )
              )?.id ||
              null;
          }

          return payload;
        }
      ),
      'Copying assignment settings'
    );
  }

  if (
    sourceTypeDescriptions.length
  ) {
    await insertRows(
      'course_assignment_type_descriptions',
      sourceTypeDescriptions.map(
        row => ({
          ...omitRow(row),
          course_id:
            targetCourseId
        })
      ),
      'Copying assignment descriptions'
    );
  }

  if (
    sourceTypeWeights.length
  ) {
    await insertRows(
      'course_assignment_type_weights',
      sourceTypeWeights.map(
        row => ({
          ...omitRow(row),
          course_id:
            targetCourseId
        })
      ),
      'Copying assignment weights'
    );
  }

  const sourceAssignmentIds =
    sourceAssignments
      .map(row => row.id)
      .filter(Boolean);

  const assignmentFilter =
    inFilter(
      sourceAssignmentIds
    );

  const [
    sourceGroupDeadlines,
    sourceGroupDescriptions,
    sourceScheduleItems,
    structuredSections,
    structuredInstances
  ] =
    assignmentFilter
      ? await Promise.all([
          rows(
            'course_assignment_group_deadlines',
            `course_assignment_id=${assignmentFilter}&select=*`,
            'Loading assignment group deadlines'
          ),

          rows(
            'assignment_group_descriptions',
            `course_assignment_id=${assignmentFilter}&select=*`,
            'Loading assignment Group descriptions'
          ),

          rows(
            'course_assignment_schedule_items',
            `course_assignment_id=${assignmentFilter}&select=*&order=display_order.asc`,
            'Loading assignment Schedule items'
          ),

          rows(
            'course_structured_sections',
            `course_assignment_id=${assignmentFilter}&select=*&order=section_order.asc`,
            'Loading Structured Form sections'
          ),

          rows(
            'course_structured_instances',
            `course_assignment_id=${assignmentFilter}&select=*&order=instance_order.asc`,
            'Loading Structured Form instances'
          )
        ])
      : [
          [],
          [],
          [],
          [],
          []
        ];

  if (
    sourceGroupDeadlines.length
  ) {
    const payloads = [];

    sourceGroupDeadlines.forEach(
      row => {
        const targetAssignment =
          assignmentMap.get(
            String(
              row.course_assignment_id
            )
          );

        if (!targetAssignment?.id) {
          return;
        }

        payloads.push({
          ...omitRow(
            row,
            [
              'course_assignment_id',
              'open_date',
              'due_date'
            ]
          ),

          course_assignment_id:
            targetAssignment.id,

          open_date:
            row.open_date
              ? mapCourseDate(
                  row.open_date,
                  mapping
                    .sourceGroups,
                  mapping
                    .targetGroups
                )
              : null,

          due_date:
            row.due_date
              ? mapCourseDate(
                  row.due_date,
                  mapping
                    .sourceGroups,
                  mapping
                    .targetGroups
                )
              : null
        });
      }
    );

    if (payloads.length) {
      await insertRows(
        'course_assignment_group_deadlines',
        payloads,
        'Copying assignment group deadlines'
      );
    }
  }

  if (
    sourceGroupDescriptions.length
  ) {
    const payloads = [];

    sourceGroupDescriptions.forEach(
      row => {
        const targetAssignment =
          assignmentMap.get(
            String(
              row.course_assignment_id
            )
          );

        if (!targetAssignment?.id) {
          return;
        }

        payloads.push({
          ...omitRow(
            row,
            [
              'course_assignment_id'
            ]
          ),
          course_assignment_id:
            targetAssignment.id
        });
      }
    );

    if (payloads.length) {
      await insertRows(
        'assignment_group_descriptions',
        payloads,
        'Copying Assignment Group descriptions'
      );
    }
  }

  if (
    sourceStreamLabels.length
  ) {
    await insertRows(
      'course_stream_labels',
      sourceStreamLabels.map(
        row => ({
          ...omitRow(row),
          course_id:
            targetCourseId,
          name:
            `Stream ${
              row.stream_number
            }`
        })
      ),
      'Copying Stream structure'
    );
  }

  if (
    sourceGroupLabels.length
  ) {
    await insertRows(
      'course_group_labels',
      sourceGroupLabels.map(
        row => ({
          ...omitRow(row),
          course_id:
            targetCourseId,
          name:
            `Group ${
              row.group_number
            }`
        })
      ),
      'Copying Group structure'
    );
  }

  const attendanceGroupMap =
    await copyOneToOneRows(
      'attendance_groups',
      sourceAttendanceGroups,
      row => ({
        ...omitRow(row),
        course_id:
          targetCourseId
      }),
      'Copying attendance group'
    );

  const sourceAttendanceGroupIds =
    sourceAttendanceGroups
      .map(row => row.id)
      .filter(Boolean);

  const attendanceGroupFilter =
    inFilter(
      sourceAttendanceGroupIds
    );

  if (
    attendanceGroupFilter
  ) {
    const sourceGroupWeeks =
      await rows(
        'attendance_group_weeks',
        `attendance_group_id=${attendanceGroupFilter}&select=*`,
        'Loading attendance group schedule'
      );

    const payloads = [];

    sourceGroupWeeks.forEach(
      row => {
        const targetGroup =
          attendanceGroupMap.get(
            String(
              row.attendance_group_id
            )
          );

        const targetWeek =
          mapping.bySourceId.get(
            String(
              row.course_week_id
            )
          );

        if (
          targetGroup?.id &&
          targetWeek?.id
        ) {
          payloads.push({
            ...omitRow(
              row,
              [
                'attendance_group_id',
                'course_week_id'
              ]
            ),
            attendance_group_id:
              targetGroup.id,
            course_week_id:
              targetWeek.id
          });
        }
      }
    );

    if (payloads.length) {
      await insertRows(
        'attendance_group_weeks',
        payloads,
        'Copying attendance group schedule'
      );
    }
  }

  const structuredSectionMap =
    await copyOneToOneRows(
      'course_structured_sections',
      structuredSections,
      row => {
        const targetAssignment =
          assignmentMap.get(
            String(
              row.course_assignment_id
            )
          );

        if (!targetAssignment?.id) {
          return null;
        }

        return {
          ...omitRow(
            row,
            [
              'course_assignment_id'
            ]
          ),
          course_assignment_id:
            targetAssignment.id
        };
      },
      'Copying Structured Form section'
    );

  const structuredInstanceMap =
    await copyOneToOneRows(
      'course_structured_instances',
      structuredInstances,
      row => {
        const targetAssignment =
          assignmentMap.get(
            String(
              row.course_assignment_id
            )
          );

        if (!targetAssignment?.id) {
          return null;
        }

        return {
          ...omitRow(
            row,
            [
              'course_assignment_id',
              'open_date',
              'close_date'
            ]
          ),

          course_assignment_id:
            targetAssignment.id,

          open_date:
            row.open_date
              ? mapCourseDate(
                  row.open_date,
                  mapping
                    .sourceGroups,
                  mapping
                    .targetGroups
                )
              : null,

          close_date:
            row.close_date
              ? mapCourseDate(
                  row.close_date,
                  mapping
                    .sourceGroups,
                  mapping
                    .targetGroups
                )
              : null
        };
      },
      'Copying Structured Form instance'
    );

  const sourceStructuredInstanceIds =
    structuredInstances
      .map(row => row.id)
      .filter(Boolean);

  const structuredInstanceFilter =
    inFilter(
      sourceStructuredInstanceIds
    );

  if (
    structuredInstanceFilter
  ) {
    const [
      instanceSectionLinks,
      instanceGroupDeadlines,
      structuredScheduleItems
    ] =
      await Promise.all([
        rows(
          'course_structured_instance_sections',
          `course_structured_instance_id=${structuredInstanceFilter}&select=*`,
          'Loading Structured Form instance sections'
        ),

        rows(
          'course_structured_instance_group_deadlines',
          `course_structured_instance_id=${structuredInstanceFilter}&select=*`,
          'Loading Structured Form group deadlines'
        ),

        rows(
          'course_structured_instance_schedule_items',
          `course_structured_instance_id=${structuredInstanceFilter}&select=*&order=display_order.asc`,
          'Loading Structured Form Schedule items'
        )
      ]);

    const linkPayloads = [];

    instanceSectionLinks.forEach(
      row => {
        const targetInstance =
          structuredInstanceMap.get(
            String(
              row.course_structured_instance_id
            )
          );

        const targetSection =
          structuredSectionMap.get(
            String(
              row.course_structured_section_id
            )
          );

        if (
          targetInstance?.id &&
          targetSection?.id
        ) {
          linkPayloads.push({
            ...omitRow(
              row,
              [
                'course_structured_instance_id',
                'course_structured_section_id'
              ]
            ),
            course_structured_instance_id:
              targetInstance.id,
            course_structured_section_id:
              targetSection.id
          });
        }
      }
    );

    if (linkPayloads.length) {
      await insertRows(
        'course_structured_instance_sections',
        linkPayloads,
        'Copying Structured Form section links'
      );
    }

    const deadlinePayloads = [];

    instanceGroupDeadlines.forEach(
      row => {
        const targetInstance =
          structuredInstanceMap.get(
            String(
              row.course_structured_instance_id
            )
          );

        if (!targetInstance?.id) {
          return;
        }

        deadlinePayloads.push({
          ...omitRow(
            row,
            [
              'course_structured_instance_id',
              'open_date',
              'close_date'
            ]
          ),

          course_structured_instance_id:
            targetInstance.id,

          open_date:
            row.open_date
              ? mapCourseDate(
                  row.open_date,
                  mapping
                    .sourceGroups,
                  mapping
                    .targetGroups
                )
              : null,

          close_date:
            row.close_date
              ? mapCourseDate(
                  row.close_date,
                  mapping
                    .sourceGroups,
                  mapping
                    .targetGroups
                )
              : null
        });
      }
    );

    if (
      deadlinePayloads.length
    ) {
      await insertRows(
        'course_structured_instance_group_deadlines',
        deadlinePayloads,
        'Copying Structured Form group deadlines'
      );
    }

    const structuredScheduleMap =
      new Map();

    const delayedStructuredDuplicates = [];

    for (
      const sourceItem of
        structuredScheduleItems
    ) {
      const targetInstance =
        structuredInstanceMap.get(
          String(
            sourceItem
              .course_structured_instance_id
          )
        );

      if (!targetInstance?.id) {
        continue;
      }

      const payload = {
        ...omitRow(
          sourceItem,
          [
            'course_structured_instance_id',
            'week_reading_assignment_id',
            'course_event_id',
            'duplicate_source_schedule_item_id'
          ]
        ),

        course_structured_instance_id:
          targetInstance.id,

        week_reading_assignment_id:
          sourceItem
            .week_reading_assignment_id
            ? (
                weekReadingMap.get(
                  String(
                    sourceItem
                      .week_reading_assignment_id
                  )
                )?.id ||
                null
              )
            : null,

        course_event_id:
          sourceItem
            .course_event_id
            ? (
                eventMap.get(
                  String(
                    sourceItem
                      .course_event_id
                  )
                )?.id ||
                null
              )
            : null
      };

      const created =
        await insertRows(
          'course_structured_instance_schedule_items',
          payload,
          'Copying Structured Form Schedule item'
        );

      const targetItem =
        created?.[0] || null;

      if (
        sourceItem.id &&
        targetItem?.id
      ) {
        structuredScheduleMap.set(
          String(
            sourceItem.id
          ),
          targetItem
        );

        if (
          sourceItem
            .duplicate_source_schedule_item_id
        ) {
          delayedStructuredDuplicates.push({
            targetId:
              targetItem.id,
            sourceDuplicateId:
              sourceItem
                .duplicate_source_schedule_item_id
          });
        }
      }
    }

    for (
      const duplicate of
        delayedStructuredDuplicates
    ) {
      const targetSource =
        structuredScheduleMap.get(
          String(
            duplicate
              .sourceDuplicateId
          )
        );

      if (targetSource?.id) {
        await patchRows(
          'course_structured_instance_schedule_items',
          `id=eq.${encodeURIComponent(
            duplicate.targetId
          )}`,
          {
            duplicate_source_schedule_item_id:
              targetSource.id
          },
          'Restoring Structured Form Schedule duplicate link'
        );
      }
    }
  }

  const duplicateFollowerIds =
    new Set(
      (Array.isArray(
        duplicateLinks
      )
        ? duplicateLinks
        : [])
        .map(
          row =>
            String(
              row
                .course_assignment_id ||
              ''
            )
        )
        .filter(Boolean)
    );

  const assignmentScheduleMap =
    new Map();

  const delayedAssignmentDuplicates = [];

  for (
    const sourceItem of
      sourceScheduleItems
  ) {
    if (
      duplicateFollowerIds.has(
        String(
          sourceItem
            .course_assignment_id
        )
      )
    ) {
      continue;
    }

    const targetAssignment =
      assignmentMap.get(
        String(
          sourceItem
            .course_assignment_id
        )
      );

    if (!targetAssignment?.id) {
      continue;
    }

    const payload = {
      ...omitRow(
        sourceItem,
        [
          'course_assignment_id',
          'week_reading_assignment_id',
          'course_event_id',
          'duplicate_source_schedule_item_id'
        ]
      ),

      course_assignment_id:
        targetAssignment.id,

      week_reading_assignment_id:
        sourceItem
          .week_reading_assignment_id
          ? (
              weekReadingMap.get(
                String(
                  sourceItem
                    .week_reading_assignment_id
                )
              )?.id ||
              null
            )
          : null,

      course_event_id:
        sourceItem
          .course_event_id
          ? (
              eventMap.get(
                String(
                  sourceItem
                    .course_event_id
                )
              )?.id ||
              null
            )
          : null
    };

    const created =
      await insertRows(
        'course_assignment_schedule_items',
        payload,
        'Copying assignment Schedule item'
      );

    const targetItem =
      created?.[0] || null;

    if (
      sourceItem.id &&
      targetItem?.id
    ) {
      assignmentScheduleMap.set(
        String(
          sourceItem.id
        ),
        targetItem
      );

      if (
        sourceItem
          .duplicate_source_schedule_item_id
      ) {
        delayedAssignmentDuplicates.push({
          targetId:
            targetItem.id,
          sourceDuplicateId:
            sourceItem
              .duplicate_source_schedule_item_id
        });
      }
    }
  }

  for (
    const duplicate of
      delayedAssignmentDuplicates
  ) {
    const targetSource =
      assignmentScheduleMap.get(
        String(
          duplicate
            .sourceDuplicateId
        )
      );

    if (targetSource?.id) {
      await patchRows(
        'course_assignment_schedule_items',
        `id=eq.${encodeURIComponent(
          duplicate.targetId
        )}`,
        {
          duplicate_source_schedule_item_id:
            targetSource.id
        },
        'Restoring assignment Schedule item duplicate link'
      );
    }
  }

  const sharedTypes =
    sourceTypeSettings.filter(
      row =>
        row
          .schedule_shared_across_instances ===
        true
    );

  for (
    const setting of
      sharedTypes
  ) {
    await callRpc(
      'set_assignment_schedule_shared_mode',
      {
        p_course_id:
          targetCourseId,
        p_assignment_type_id:
          setting
            .assignment_type_id,
        p_enabled: true
      },
      'Restoring shared assignment Schedule'
    );
  }

  for (
    const link of
      duplicateLinks
  ) {
    if (
      link.sync_kind ===
      'shared_instance'
    ) {
      continue;
    }

    const targetFollower =
      assignmentMap.get(
        String(
          link
            .course_assignment_id
        )
      );

    const targetSource =
      assignmentMap.get(
        String(
          link
            .source_course_assignment_id
        )
      );

    if (
      targetFollower?.id &&
      targetSource?.id
    ) {
      await callRpc(
        'set_assignment_schedule_duplicate',
        {
          p_course_assignment_id:
            targetFollower.id,
          p_source_course_assignment_id:
            targetSource.id
        },
        'Restoring duplicated assignment Schedule'
      );
    }
  }

  activeCourseCopyJournal =
    null;

  return {
    ok: true,
    summary:
      preview.summary
  };

  } catch (err) {
    activeCourseCopyJournal =
      null;

    let rollbackError =
      null;

    try {
      await rollbackCourseCopy({
        targetCourseId,
        targetCourse,
        targetWeeks,
        targetWeekContent,
        journal:
          rollbackJournal
      });
    } catch (cleanupErr) {
      rollbackError =
        cleanupErr;
    }

    if (rollbackError) {
      throw new Error(
        (err?.message ||
          'The course import failed.') +
        ' Automatic rollback also encountered an error: ' +
        (
          rollbackError?.message ||
          'unknown rollback error'
        )
      );
    }

    throw new Error(
      (err?.message ||
        'The course import failed.') +
      ' The target course was restored to its pre-import state.'
    );
  }
}
